// The WebGL2 context, its frame, and the one registry that survives a
// context loss (the plan's A.2 `gl/context.ts`).
//
// It is the WebGL2 half of `gfx/types.ts` `FrameGfx`: the engine and every
// layer see the interface, and this file is the only one that holds the raw
// context. The sizing is shared with the WebGPU context and lives in
// `gfx/surface.ts`.
//
// Three rules live here and nowhere else:
//
// 1. **One blend state.** `blendFunc(ONE, ONE_MINUS_SRC_ALPHA)` with
//    premultiplied output. Every fragment shader in the fast client writes
//    premultiplied rgba, and additive glow is a per-instance flag rather
//    than a second state (layer.ts `F_ADD`). An F_ADD fragment writes
//    alpha 0 in the dark identity only; on paper it is a covered ink
//    (`fast/shaders/addglow.ts`, R3297).
// 2. **`UNPACK_ALIGNMENT` is 1.** The core grid is 133 columns wide, which
//    is odd, so a one-byte-per-cell row is not 4-byte aligned. The default
//    alignment of 4 would shear every data texture.
// 3. **Every GPU object is a `GpuResource`.** A lost context destroys them
//    all; the restore recreates them in the order they were registered, so
//    a batch that needs its program can rely on the program existing. The
//    list is the `gfx/registry.ts` both backends share (R1369).

import { readQueryKnobs, type QueryKnobs } from '../knobs';
import { Surface, hasQueryFlag } from '../gfx/surface';
import { GpuTimerRing } from '../../perf/gputimer';
import { ResourceRegistry, type GfxResource } from '../gfx/registry';
import type { DrawnSize, FrameGfx } from '../gfx/types';

/**
 * One GPU object the context owns: the shared resource shape over the
 * WebGL2 context (R1369).
 *
 * `create` builds the object on the live context; `destroy` drops the
 * handles. Both must be safe to call twice.
 */
export type GpuResource = GfxResource<WebGL2RenderingContext>;

/** The context wrapper every module of the WebGL2 backend is handed. */
export interface GL extends FrameGfx {
  readonly backend: 'webgl2';
  /** The live context. It is the same object across a loss and a restore. */
  readonly gl: WebGL2RenderingContext;
  register(r: GpuResource): void;
  unregister(r: GpuResource): void;
}

/**
 * The attribute set the engine asks for.
 *
 * The client ships WITHOUT multisampling (R491). The owner accepted the
 * picture on 2026-09-04: the fast client antialiases its lines in the
 * shader, and MSAA was 44 to 57 % of the frame in fill.
 *
 * `?aa=1` is the one thing that changes it, and it changes only `antialias`
 * (R431, R491). The knob measures what multisampling costs in fill; it is
 * not a setting, and nothing in the client writes it.
 */
const CONTEXT_ATTRIBUTES: WebGLContextAttributes = {
  alpha: false,
  antialias: false,
  premultipliedAlpha: true,
  powerPreference: 'high-performance',
  depth: false,
  stencil: false,
  preserveDrawingBuffer: false,
};

/**
 * The shared timer ring as a `GpuResource` (R431).
 *
 * The ring itself lives in `perf/gputimer.ts`, because the PixiJS client
 * reported the same column from the same code. `perf/` knows nothing about
 * this engine, so this adapter is the whole of what the engine adds: it
 * registers the ring with the context, which rebuilds it after a loss.
 */
class TimerResource implements GpuResource {
  readonly ring: GpuTimerRing;

  constructor(lost: () => boolean) {
    this.ring = new GpuTimerRing(lost);
  }

  create(gl: WebGL2RenderingContext): void { this.ring.create(gl); }
  destroy(gl: WebGL2RenderingContext): void { this.ring.destroy(gl); }
}

class GLContext implements GL {
  readonly backend = 'webgl2' as const;
  readonly gl: WebGL2RenderingContext;
  readonly glcheck: boolean;
  readonly aa: boolean;
  lost = false;
  renderer = 'unknown';
  drawCalls = 0;
  instances = 0;

  private readonly surface: Surface;
  /**
   * A lost context's handles are gone, so `unregister` and `dispose` destroy
   * nothing while the context is lost.
   */
  private readonly registry = new ResourceRegistry<WebGL2RenderingContext>({
    handle: () => this.gl,
    lost: () => this.lost,
    destroyWhileLost: false,
  });
  private loseExt: WEBGL_lose_context | null = null;
  private timer: TimerResource;
  private disposed = false;

  constructor(readonly canvas: HTMLCanvasElement, query?: QueryKnobs) {
    // Both knobs are read before the context exists, so they come from the
    // query string and a reload is what applies them (R431). The default is
    // no multisampling, and `?aa=1` puts it back (R491).
    const knobs = query ?? readQueryKnobs();
    this.aa = knobs.aa;
    const attrs = knobs.aa
      ? { ...CONTEXT_ATTRIBUTES, antialias: true }
      : CONTEXT_ATTRIBUTES;
    const gl = canvas.getContext('webgl2', attrs);
    if (!gl) throw new Error('fast: WebGL2 is not available');
    this.gl = gl;
    this.glcheck = hasQueryFlag('glcheck');
    this.loseExt = gl.getExtension('WEBGL_lose_context');

    const info = gl.getExtension('WEBGL_debug_renderer_info');
    this.renderer = String(gl.getParameter(info ? info.UNMASKED_RENDERER_WEBGL : gl.RENDERER));

    canvas.addEventListener('webglcontextlost', this.onLost, false);
    canvas.addEventListener('webglcontextrestored', this.onRestored, false);

    this.applyState();
    // The timer registers FIRST, so it is rebuilt before any program after a
    // restore. It measures every device pixel the frame writes (R431).
    this.timer = new TimerResource(() => this.lost);
    this.register(this.timer);
    this.surface = new Surface(canvas, knobs.dpr ?? undefined);
  }

  // -- size ----------------------------------------------------------------

  get width(): number { return this.surface.width; }
  get height(): number { return this.surface.height; }
  get pixelWidth(): number { return this.surface.pixelWidth; }
  get pixelHeight(): number { return this.surface.pixelHeight; }
  get dpr(): number { return this.surface.dpr; }

  onResize(fn: () => void) { this.surface.onResize(fn); }
  onRestore(fn: () => void) { this.registry.onRestore(fn); }

  /** The drawing buffer, read when asked (R1498). A lost context answers 0x0. */
  drawnSize(): DrawnSize {
    return { what: 'dbuf', w: this.gl.drawingBufferWidth, h: this.gl.drawingBufferHeight };
  }

  // -- state ---------------------------------------------------------------

  /** The one blend state and the one unpack alignment. Set on every restore. */
  private applyState() {
    const gl = this.gl;
    gl.disable(gl.DEPTH_TEST);
    gl.disable(gl.STENCIL_TEST);
    gl.disable(gl.CULL_FACE);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    // Data uploads are one byte per cell on an odd-width grid. Set it before
    // any upload, and never set it back.
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
    gl.clearColor(0, 0, 0, 1);
  }

  // -- the frame -----------------------------------------------------------

  /**
   * Opens the frame: the timer, the viewport and the clear.
   *
   * The timer opens BEFORE the clear, so `gpuMs` holds every device pixel
   * the frame writes and not only the draw pass. The clear is a
   * screen-sized write and the report reads a floor, so the floor must
   * contain it (R431).
   */
  beginFrame(clear: boolean, clearRgb: readonly [number, number, number]): void {
    if (this.lost) return;
    const gl = this.gl;
    this.timer.ring.begin();
    gl.viewport(0, 0, this.pixelWidth, this.pixelHeight);
    if (clear) {
      gl.clearColor(clearRgb[0], clearRgb[1], clearRgb[2], 1);
      gl.clear(gl.COLOR_BUFFER_BIT);
    }
  }

  endFrame(): void {
    if (this.lost) return;
    this.timer.ring.end();
    this.timer.ring.poll();
  }

  setBlend(on: boolean): void {
    if (this.lost) return;
    const gl = this.gl;
    if (on) gl.enable(gl.BLEND);
    else gl.disable(gl.BLEND);
  }

  get gpuMs(): number | null { return this.timer.ring.lastMs; }

  // -- resources -----------------------------------------------------------

  register(r: GpuResource) { this.registry.register(r); }
  unregister(r: GpuResource) { this.registry.unregister(r); }

  private onLost = (e: Event) => {
    // The default action refuses the restore, so it must be prevented.
    e.preventDefault();
    this.lost = true;
    this.registry.destroyAll();
  };

  private onRestored = () => {
    this.lost = false;
    this.applyState();
    // Registration order is dependency order: a batch registered after its
    // program finds the program already rebuilt.
    this.registry.restore();
  };

  // -- diagnostics ---------------------------------------------------------

  check(where: string) {
    if (!this.glcheck || this.lost) return;
    const err = this.gl.getError();
    if (err !== this.gl.NO_ERROR) {
      // eslint-disable-next-line no-console
      console.error(`fast: GL error 0x${err.toString(16)} after ${where}`);
    }
  }

  loseContext() { this.loseExt?.loseContext(); }
  restoreContext() { this.loseExt?.restoreContext(); }

  dispose() {
    if (this.disposed) return;
    this.disposed = true;
    this.surface.dispose();
    this.canvas.removeEventListener('webglcontextlost', this.onLost);
    this.canvas.removeEventListener('webglcontextrestored', this.onRestored);
    this.registry.dispose();
  }
}

/** Builds the one WebGL2 context of the fast client. */
export function createGL(canvas: HTMLCanvasElement, knobs?: QueryKnobs): GL {
  return new GLContext(canvas, knobs);
}
